import { useEffect, useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { AgentDirectoryPerson } from "@/lib/agentsDemoData";
import { cn } from "@/lib/utils";

type PeopleSearchMultiSelectProps = {
  people: AgentDirectoryPerson[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyDirectoryMessage?: string;
};

export function PeopleSearchMultiSelect({
  people,
  selectedIds,
  onChange,
  disabled = false,
  placeholder = "Search by name or email",
  emptyDirectoryMessage = "No other people in this organization.",
}: PeopleSearchMultiSelectProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () =>
      selectedIds
        .map((id) => people.find((person) => person.id === id))
        .filter((person): person is AgentDirectoryPerson => Boolean(person)),
    [people, selectedIds],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = people.filter((person) => !selectedIds.includes(person.id));
    if (!q) return available.slice(0, 8);
    return available
      .filter(
        (person) =>
          person.name.toLowerCase().includes(q) || person.email.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [people, query, selectedIds]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const addPerson = (id: string) => {
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setQuery("");
    setOpen(true);
  };

  const removePerson = (id: string) => {
    onChange(selectedIds.filter((current) => current !== id));
  };

  if (people.length === 0) {
    return <p className="mt-2 text-sm text-gray-500">{emptyDirectoryMessage}</p>;
  }

  return (
    <div ref={rootRef} className="mt-2 space-y-2">
      {selected.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {selected.map((person) => (
            <li
              key={person.id}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-gray-50 py-1 pl-2.5 pr-1 text-xs text-gray-800 dark:border-white/15 dark:bg-white/5 dark:text-gray-100"
            >
              <span className="truncate">
                {person.name}
                <span className="text-gray-500"> · {person.email}</span>
              </span>
              <button
                type="button"
                disabled={disabled}
                className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label={`Remove ${person.name}`}
                onClick={() => removePerson(person.id)}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="relative">
        <Input
          className="w-full"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (matches[0]) addPerson(matches[0].id);
            }
          }}
        />
        {open && !disabled ? (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-zinc-900"
          >
            {matches.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">
                {query.trim() ? "No matching people." : "Everyone available is already shared."}
              </li>
            ) : (
              matches.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    role="option"
                    className={cn(
                      "flex w-full flex-col items-start px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5",
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => addPerson(person.id)}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {person.name}
                    </span>
                    <span className="text-xs text-gray-500">{person.email}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
